"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import type { ClientProposal } from "../proposalTypes";
import { openProposalPrint } from "../buildProposalPrintHtml";
import {
  PROPOSAL_FONTS_HREF,
  proposalDisplay,
  proposalFont,
  proposalTokens,
} from "../proposalTheme";

function Section({
  id,
  children,
  soft,
}: {
  id?: string;
  children: ReactNode;
  soft?: boolean;
}) {
  return (
    <Box
      id={id}
      component="section"
      sx={{
        bgcolor: soft ? proposalTokens.bgSoft : proposalTokens.bg,
        px: { xs: 2.5, md: 4 },
        py: { xs: 6, md: 10 },
      }}
    >
      <Box sx={{ maxWidth: proposalTokens.maxWidth, mx: "auto" }}>{children}</Box>
    </Box>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: proposalTokens.accent,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: proposalDisplay,
        fontWeight: 500,
        color: proposalTokens.text,
        fontSize: { xs: "1.85rem", md: "2.6rem" },
        lineHeight: 1.12,
        letterSpacing: "-0.02em",
        mb: 2,
        maxWidth: 720,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({ children, sx }: { children: ReactNode; sx?: object }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        color: proposalTokens.textMuted,
        fontSize: { xs: "1rem", md: "1.05rem" },
        lineHeight: 1.65,
        maxWidth: 640,
        mb: 3,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function SelectCard({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "block",
        width: "100%",
        boxSizing: "border-box",
        p: { xs: 2.25, md: 2.75 },
        borderRadius: "4px",
        border: `1px solid ${selected ? proposalTokens.accent : proposalTokens.border}`,
        bgcolor: selected ? proposalTokens.accentSoft : "transparent",
        transition: "border-color 160ms ease, background 160ms ease",
        "&:hover": {
          borderColor: selected
            ? proposalTokens.accent
            : "rgba(244, 240, 234, 0.28)",
        },
        "&:focus-visible": {
          outline: `2px solid ${proposalTokens.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      {children}
    </Box>
  );
}

export default function ProposalExperience({ proposal }: { proposal: ClientProposal }) {
  const [venueId, setVenueId] = useState<string | null>(null);
  const [dateId, setDateId] = useState<string | null>(null);
  const [entertainmentId, setEntertainmentId] = useState<string>(
    proposal.entertainmentOptions[0]?.id ?? ""
  );
  const [printBusy, setPrintBusy] = useState(false);

  useEffect(() => {
    const existing = document.querySelector(`link[href="${PROPOSAL_FONTS_HREF}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PROPOSAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);

  const selectedVenue = useMemo(
    () => proposal.venues.find((v) => v.id === venueId) ?? null,
    [proposal.venues, venueId]
  );
  const selectedDate = useMemo(
    () => proposal.dateOptions.find((d) => d.id === dateId) ?? null,
    [proposal.dateOptions, dateId]
  );
  const selectedEntertainment = useMemo(
    () =>
      proposal.entertainmentOptions.find((e) => e.id === entertainmentId) ??
      proposal.entertainmentOptions[0] ??
      null,
    [proposal.entertainmentOptions, entertainmentId]
  );

  function handlePrint() {
    setPrintBusy(true);
    try {
      openProposalPrint(proposal, {
        venueName: selectedVenue?.name ?? null,
        dateLabel: selectedDate?.label ?? null,
        entertainmentLabel: selectedEntertainment?.label ?? null,
      });
    } finally {
      window.setTimeout(() => setPrintBusy(false), 800);
    }
  }

  return (
    <Box
      component="main"
      sx={{
        bgcolor: proposalTokens.bg,
        color: proposalTokens.text,
        minHeight: "100vh",
        "@media print": {
          "& .no-print": { display: "none !important" },
        },
      }}
    >
      {/* A. Hero */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "88vh", md: "92vh" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={proposal.heroImage}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 30%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(12,11,10,0.35) 0%, rgba(12,11,10,0.55) 40%, rgba(12,11,10,0.94) 100%)",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 2.5, md: 4 },
            pb: { xs: 5, md: 7 },
            pt: { xs: 5, md: 6 },
            maxWidth: proposalTokens.maxWidth,
            mx: "auto",
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: proposalTokens.accent,
              mb: 2,
            }}
          >
            {proposal.heroEyebrow}
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: proposalDisplay,
              fontWeight: 500,
              fontSize: { xs: "2.4rem", md: "4rem" },
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: proposalTokens.text,
              maxWidth: 780,
              mb: 2,
            }}
          >
            {proposal.heroHeadline}
          </Typography>
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: { xs: "1.05rem", md: "1.2rem" },
              lineHeight: 1.55,
              color: proposalTokens.warm,
              maxWidth: 560,
              mb: 3.5,
            }}
          >
            {proposal.heroSubheadline}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 3 }}>
            {proposal.summaryItems.map((item) => (
              <Box
                key={item}
                sx={{
                  border: `1px solid ${proposalTokens.border}`,
                  px: 1.5,
                  py: 0.75,
                  fontFamily: proposalFont,
                  fontSize: "0.8rem",
                  color: proposalTokens.text,
                  letterSpacing: "0.02em",
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
          <Box className="no-print" sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <Button
              onClick={handlePrint}
              disabled={printBusy}
              startIcon={<PictureAsPdfOutlinedIcon />}
              sx={{
                fontFamily: proposalFont,
                fontWeight: 700,
                textTransform: "none",
                bgcolor: proposalTokens.accent,
                color: "#0c0b0a",
                borderRadius: "4px",
                px: 2.5,
                py: 1.15,
                "&:hover": { bgcolor: proposalTokens.accent, filter: "brightness(1.08)" },
              }}
            >
              Download Proposal
            </Button>
            <Button
              href="#preferences"
              sx={{
                fontFamily: proposalFont,
                fontWeight: 600,
                textTransform: "none",
                color: proposalTokens.text,
                border: `1px solid ${proposalTokens.border}`,
                borderRadius: "4px",
                px: 2.5,
                py: 1.15,
                "&:hover": { borderColor: proposalTokens.accent, bgcolor: "transparent" },
              }}
            >
              Choose preferences
            </Button>
          </Box>
        </Box>
      </Box>

      {/* B. Experience pillars */}
      <Section>
        <Eyebrow>The experience</Eyebrow>
        <Heading>{proposal.experienceTitle}</Heading>
        <Body>{proposal.experienceIntro}</Body>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 3, md: 2.5 },
            mt: 2,
          }}
        >
          {proposal.experiencePillars.map((pillar) => (
            <Box key={pillar.number}>
              <Box
                component="img"
                src={pillar.image}
                alt={pillar.imageAlt}
                sx={{
                  width: "100%",
                  height: { xs: 200, md: 240 },
                  objectFit: "cover",
                  mb: 2,
                  display: "block",
                }}
              />
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  fontSize: "0.7rem",
                  letterSpacing: "0.14em",
                  color: proposalTokens.accent,
                  mb: 0.75,
                }}
              >
                {pillar.number}
              </Typography>
              <Typography
                sx={{
                  fontFamily: proposalDisplay,
                  fontSize: "1.35rem",
                  color: proposalTokens.text,
                  mb: 1,
                }}
              >
                {pillar.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  color: proposalTokens.textMuted,
                  fontSize: "0.95rem",
                  lineHeight: 1.55,
                }}
              >
                {pillar.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {/* C. Featured case study + venues */}
      <Section id="venues" soft>
        <Eyebrow>Proof of scale</Eyebrow>
        <Heading>What a 50–60 person night looks like.</Heading>
        <Body>
          Before you pick a room, here is a real ComedyUO night at the same headcount as Beazley.
          Then choose from the Manhattan options below.
        </Body>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
            gap: { xs: 2.5, md: 3 },
            mb: { xs: 5, md: 7 },
            alignItems: "stretch",
          }}
        >
          <Box
            component="img"
            src={proposal.featuredCaseStudy.photo}
            alt={proposal.featuredCaseStudy.photoAlt}
            sx={{
              width: "100%",
              height: { xs: 260, md: "100%" },
              minHeight: { md: 360 },
              objectFit: "cover",
              display: "block",
            }}
          />
          <Box
            sx={{
              bgcolor: proposalTokens.bgElevated,
              border: `1px solid ${proposalTokens.border}`,
              p: { xs: 2.5, md: 3.5 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: proposalFont,
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: proposalTokens.accent,
                mb: 1.25,
              }}
            >
              {proposal.featuredCaseStudy.eyebrow}
            </Typography>
            <Typography
              sx={{
                fontFamily: proposalDisplay,
                fontSize: { xs: "1.55rem", md: "1.85rem" },
                color: proposalTokens.text,
                lineHeight: 1.15,
                mb: 1.5,
              }}
            >
              {proposal.featuredCaseStudy.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: proposalFont,
                fontSize: "0.85rem",
                fontWeight: 600,
                color: proposalTokens.warm,
                mb: 1.5,
              }}
            >
              {proposal.featuredCaseStudy.venueName} · {proposal.featuredCaseStudy.guestCount}
            </Typography>
            <Typography
              sx={{
                fontFamily: proposalFont,
                fontSize: "0.98rem",
                color: proposalTokens.textMuted,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              {proposal.featuredCaseStudy.body}
            </Typography>
            <Typography
              sx={{
                fontFamily: proposalFont,
                fontSize: "0.78rem",
                color: proposalTokens.textFaint,
                letterSpacing: "0.04em",
              }}
            >
              {proposal.featuredCaseStudy.eventLabel}
            </Typography>
            <Button
              className="no-print"
              onClick={() => setVenueId(proposal.featuredCaseStudy.venueId)}
              sx={{
                mt: 2.5,
                alignSelf: "flex-start",
                fontFamily: proposalFont,
                fontWeight: 700,
                textTransform: "none",
                bgcolor: proposalTokens.accent,
                color: "#0c0b0a",
                borderRadius: "4px",
                px: 2.25,
                py: 1,
                "&:hover": { bgcolor: proposalTokens.accent, filter: "brightness(1.08)" },
              }}
            >
              Prefer this room
            </Button>
          </Box>
        </Box>

        <Eyebrow>Venue options</Eyebrow>
        <Heading>Rooms sized for 50–60 guests.</Heading>
        <Body>
          Manhattan options that fit the headcount, from distillery and rooftop to The Seville,
          Skewr, and DCTV Firehouse. Where we have already produced a night, you will see a photo
          from that event. Availability and final pricing remain subject to confirmation.
        </Body>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {proposal.venues.map((venue) => {
            const selected = venueId === venue.id;
            return (
              <SelectCard
                key={venue.id}
                selected={selected}
                onClick={() => setVenueId(venue.id)}
              >
                <Box
                  component="img"
                  src={venue.heroImage}
                  alt={venue.name}
                  sx={{
                    width: "100%",
                    height: { xs: 180, md: 220 },
                    objectFit: "cover",
                    mb: 2,
                    display: "block",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: proposalFont,
                      fontSize: "0.7rem",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: proposalTokens.accent,
                    }}
                  >
                    {venue.neighborhood}
                  </Typography>
                  {selected ? (
                    <CheckIcon sx={{ fontSize: 18, color: proposalTokens.accent }} />
                  ) : null}
                </Box>
                <Typography
                  sx={{
                    fontFamily: proposalDisplay,
                    fontSize: "1.45rem",
                    color: proposalTokens.text,
                    mb: 0.5,
                  }}
                >
                  {venue.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.95rem",
                    color: proposalTokens.warm,
                    mb: 1.25,
                  }}
                >
                  {venue.positioning}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.92rem",
                    color: proposalTokens.textMuted,
                    lineHeight: 1.55,
                    mb: 1.5,
                  }}
                >
                  {venue.description}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 1.5 }}>
                  {venue.highlights.map((h) => (
                    <Typography
                      component="li"
                      key={h}
                      sx={{
                        fontFamily: proposalFont,
                        fontSize: "0.88rem",
                        color: proposalTokens.textMuted,
                        mb: 0.4,
                      }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>
                {venue.caseStudy ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "88px 1fr",
                      gap: 1.5,
                      borderTop: `1px solid ${proposalTokens.border}`,
                      pt: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src={venue.caseStudy.photo}
                      alt={venue.caseStudy.photoAlt}
                      sx={{
                        width: 88,
                        height: 88,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: proposalFont,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: proposalTokens.accent,
                          mb: 0.5,
                        }}
                      >
                        {venue.caseStudy.eyebrow} · {venue.caseStudy.guestCount}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: proposalFont,
                          fontSize: "0.88rem",
                          fontWeight: 600,
                          color: proposalTokens.text,
                          mb: 0.5,
                          lineHeight: 1.3,
                        }}
                      >
                        {venue.caseStudy.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: proposalFont,
                          fontSize: "0.8rem",
                          color: proposalTokens.textMuted,
                          lineHeight: 1.45,
                        }}
                      >
                        {venue.caseStudy.body}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.8rem",
                    color: proposalTokens.textFaint,
                    borderTop: venue.caseStudy
                      ? "none"
                      : `1px solid ${proposalTokens.border}`,
                    pt: venue.caseStudy ? 0 : 1.25,
                  }}
                >
                  {venue.availability} · {venue.guestFit}
                </Typography>
              </SelectCard>
            );
          })}
        </Box>
      </Section>

      {/* D. Entertainment */}
      <Section id="entertainment">
        <Eyebrow>Entertainment formats</Eyebrow>
        <Heading>{proposal.entertainmentTitle}</Heading>
        <Body>{proposal.entertainmentIntro}</Body>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: proposal.entertainmentOptions.length >= 3 ? "repeat(3, 1fr)" : "1fr 1fr",
            },
            gap: 2.5,
          }}
        >
          {proposal.entertainmentOptions.map((opt) => {
            const selected = entertainmentId === opt.id;
            return (
              <SelectCard
                key={opt.id}
                selected={selected}
                onClick={() => setEntertainmentId(opt.id)}
              >
                <Box
                  component="img"
                  src={opt.image}
                  alt={opt.imageAlt}
                  sx={{
                    width: "100%",
                    height: { xs: 180, md: 220 },
                    objectFit: "cover",
                    mb: 2,
                    display: "block",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: proposalFont,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: proposalTokens.accent,
                        mb: 0.5,
                      }}
                    >
                      {opt.tagline}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: proposalDisplay,
                        fontSize: "1.4rem",
                        color: proposalTokens.text,
                        lineHeight: 1.15,
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                  {selected ? (
                    <CheckIcon sx={{ fontSize: 18, color: proposalTokens.accent, mt: 0.5 }} />
                  ) : null}
                </Box>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.95rem",
                    color: proposalTokens.textMuted,
                    mb: 1.25,
                    lineHeight: 1.55,
                  }}
                >
                  {opt.description}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.88rem",
                    color: proposalTokens.warm,
                    mb: 1.5,
                    lineHeight: 1.5,
                  }}
                >
                  Best for: {opt.bestFor}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: proposalTokens.textFaint,
                    mb: 0.75,
                  }}
                >
                  What&apos;s included
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 1.5 }}>
                  {opt.includes.map((item) => (
                    <Typography
                      component="li"
                      key={item}
                      sx={{
                        fontFamily: proposalFont,
                        fontSize: "0.88rem",
                        color: proposalTokens.textMuted,
                        mb: 0.35,
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.82rem",
                    color: opt.pricingTbd ? proposalTokens.accent : proposalTokens.textFaint,
                    fontStyle: opt.pricingTbd ? "italic" : "normal",
                  }}
                >
                  {opt.pricingNote}
                </Typography>
              </SelectCard>
            );
          })}
        </Box>
      </Section>

      {/* E. Timeline */}
      <Section soft>
        <Eyebrow>Run of show</Eyebrow>
        <Heading>{proposal.timelineTitle}</Heading>
        <Body>{proposal.timelineNote}</Body>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {proposal.timeline.map((step, i) => (
            <Box
              key={step.time}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "88px 1fr", md: "120px 1fr" },
                gap: 2,
                py: 2.25,
                borderTop: `1px solid ${proposalTokens.border}`,
                borderBottom:
                  i === proposal.timeline.length - 1
                    ? `1px solid ${proposalTokens.border}`
                    : "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  fontWeight: 600,
                  color: proposalTokens.accent,
                  fontSize: "0.95rem",
                }}
              >
                {step.time}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontFamily: proposalDisplay,
                    fontSize: "1.2rem",
                    color: proposalTokens.text,
                    mb: 0.4,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    color: proposalTokens.textMuted,
                    fontSize: "0.95rem",
                  }}
                >
                  {step.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      {/* F. Handles */}
      <Section soft>
        <Eyebrow>Production</Eyebrow>
        <Heading>{proposal.handlesTitle}</Heading>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
            maxWidth: 720,
          }}
        >
          {proposal.handles.map((item) => (
            <Box
              key={item}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                py: 0.75,
              }}
            >
              <CheckIcon sx={{ fontSize: 18, color: proposalTokens.accent }} />
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  color: proposalTokens.text,
                  fontSize: "0.98rem",
                }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {/* G. Who we work with */}
      <Section>
        <Eyebrow>Trusted by teams like yours</Eyebrow>
        <Heading>{proposal.clientsTitle}</Heading>
        <Body>{proposal.clientsIntro}</Body>
        <Box
          sx={{
            bgcolor: "#f3efe8",
            borderRadius: "4px",
            px: { xs: 2, md: 3 },
            py: { xs: 2.5, md: 3.25 },
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "space-between" },
              gap: { xs: 3.5, md: 4 },
              minWidth: { xs: "max-content", md: "100%" },
            }}
          >
            {proposal.clientLogos.map((logo) => {
              const height = logo.height ?? 52;
              const maxWidth = logo.maxWidth ?? 170;
              return (
                <Box
                  key={logo.alt}
                  component="img"
                  src={logo.src}
                  alt={logo.alt}
                  sx={{
                    height: { xs: Math.round(height * 0.72), md: height },
                    width: "auto",
                    maxWidth: { xs: Math.round(maxWidth * 0.72), md: maxWidth },
                    objectFit: "contain",
                    flexShrink: 0,
                    // Drop baked-in white logo backgrounds against the cream panel.
                    mixBlendMode: "multiply",
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Section>

      {/* H. Dates + next steps */}
      <Section id="preferences" soft>
        <Eyebrow>Dates &amp; preferences</Eyebrow>
        <Typography
          sx={{
            fontFamily: proposalFont,
            fontWeight: 600,
            color: proposalTokens.text,
            mb: 1.25,
            mt: 1,
            fontSize: "0.9rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Preferred date
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
            mb: 4,
          }}
        >
          {proposal.dateOptions.map((d) => {
            const selected = dateId === d.id;
            return (
              <SelectCard key={d.id} selected={selected} onClick={() => setDateId(d.id)}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontFamily: proposalFont,
                      color: proposalTokens.text,
                      fontSize: "0.98rem",
                    }}
                  >
                    {d.label}
                  </Typography>
                  {selected ? (
                    <CheckIcon sx={{ fontSize: 18, color: proposalTokens.accent }} />
                  ) : null}
                </Box>
              </SelectCard>
            );
          })}
        </Box>

        <Box
          sx={{
            border: `1px solid ${proposalTokens.border}`,
            borderRadius: "4px",
            p: { xs: 2.5, md: 3 },
            bgcolor: proposalTokens.bgElevated,
            mb: 4,
            maxWidth: 640,
          }}
        >
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: proposalTokens.accent,
              mb: 2,
            }}
          >
            Preference summary
          </Typography>
          {[
            ["Venue", selectedVenue?.name ?? "Not selected"],
            ["Date", selectedDate?.label ?? "Not selected"],
            ["Entertainment", selectedEntertainment?.label ?? "Not selected"],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 1,
                borderBottom: `1px solid ${proposalTokens.border}`,
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Typography
                sx={{ fontFamily: proposalFont, color: proposalTokens.textFaint, fontSize: "0.9rem" }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  color: proposalTokens.text,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textAlign: "right",
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Heading>{proposal.nextStepsTitle}</Heading>
        <Body sx={{ mb: 3 }}>{proposal.nextStepsBody}</Body>
        <Box className="no-print" sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Button
            onClick={handlePrint}
            disabled={printBusy}
            startIcon={<PictureAsPdfOutlinedIcon />}
            sx={{
              fontFamily: proposalFont,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: proposalTokens.accent,
              color: "#0c0b0a",
              borderRadius: "4px",
              px: 2.5,
              py: 1.15,
              "&:hover": { bgcolor: proposalTokens.accent, filter: "brightness(1.08)" },
            }}
          >
            Download Proposal
          </Button>
          <Button
            href={`mailto:admin@comedyuo.com?subject=${encodeURIComponent(
              `${proposal.clientName} proposal question`
            )}`}
            sx={{
              fontFamily: proposalFont,
              fontWeight: 600,
              textTransform: "none",
              color: proposalTokens.text,
              border: `1px solid ${proposalTokens.border}`,
              borderRadius: "4px",
              px: 2.5,
              py: 1.15,
              "&:hover": { borderColor: proposalTokens.accent, bgcolor: "transparent" },
            }}
          >
            Ask a Question
          </Button>
        </Box>
      </Section>

      <Box
        component="footer"
        sx={{
          px: { xs: 2.5, md: 4 },
          py: 3,
          borderTop: `1px solid ${proposalTokens.border}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: proposalFont,
            fontSize: "0.8rem",
            color: proposalTokens.textFaint,
          }}
        >
          Prepared by ComedyUO · Confidential · {proposal.clientName}
        </Typography>
      </Box>
    </Box>
  );
}
